import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PlayerName from "@/components/PlayerName";
import { toast } from "sonner";
import { Award, CalendarDays, Loader2, Sparkles, Trophy, Wand2, X } from "lucide-react";

export default function LeagueDetail({ league, onClose }) {
  const canManage = !!league.can_manage;
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [draft, setDraft] = useState(null);
  const [pendingRegs, setPendingRegs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [st, m] = await Promise.all([
      api.get("/bpl/standings", { params: { league_id: league.id } }),
      api.get("/bpl/matches", { params: { league_id: league.id } }),
    ]);
    setStandings(st.data); setMatches(m.data);
    if (canManage) {
      const [{ data }, regs] = await Promise.all([
        api.get("/admin/league/schedule", { params: { league_id: league.id } }),
        api.get(`/admin/leagues/${league.id}/registrations`),
      ]);
      setDraft(data && data.status === "draft" ? data : null);
      setPendingRegs(regs.data.filter((r) => r.status === "pending"));
    }
  }, [league.id, canManage]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const approve = async (r) => {
    try {
      await api.post(`/admin/league/registrations/${r.id}/approve`);
      toast.success(`Team ${r.team_name} approved — they're on the ladder`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/league/schedule/generate", { league_id: league.id, weeks: league.weeks || 6 });
      setDraft(data);
      toast.success("AI schedule drafted — review and publish");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/league/schedule/${draft.id}/publish`);
      toast.success(`Season live — ${data.matches_created} matches on the board`);
      setDraft(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    try { await api.delete(`/admin/league/schedule/${draft.id}`); setDraft(null); toast.success("Draft discarded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="league-detail-modal">
      <div className="bg-pb-surface border border-pb-cyan/40 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-pb-border">
          <div>
            <div className="font-display font-black uppercase text-xl">{league.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> Starts {league.start_date} · {league.weeks} weeks · coed open ladder
            </div>
          </div>
          <button data-testid="league-detail-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {canManage && pendingRegs.length > 0 && (
            <div className="border border-amber-500/40 bg-amber-500/5 p-4" data-testid="league-pending-regs">
              <div className="font-display font-bold uppercase text-sm mb-2 text-amber-300">Roster Approvals ({pendingRegs.length})</div>
              <div className="divide-y divide-pb-border/40">
                {pendingRegs.map((r) => (
                  <div key={r.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`pending-reg-${r.id}`}>
                    <span className="flex-1"><span className="font-bold">{r.team_name}</span> — <PlayerName userId={r.user_id} name={r.user_name} />{r.partner_name ? <> & <PlayerName userId={r.partner_id} name={r.partner_name} /></> : ""}
                      {r.partner_status === "pending_payment" && <span className="text-amber-400 font-bold uppercase text-[10px] tracking-widest ml-1" data-testid={`ld-partner-pending-${r.id}`}>partner payment pending</span>}</span>
                    <button data-testid={`approve-reg-${r.id}`} onClick={() => approve(r)}
                      className="bg-amber-400 text-black font-black uppercase tracking-widest px-3 py-1.5 text-[10px] hover:bg-amber-300">Approve</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canManage && matches.length === 0 && (
            <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-4" data-testid="league-schedule-admin">
              {!draft ? (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display font-bold uppercase text-sm">Season not started</div>
                    <div className="text-xs text-zinc-500">{standings.length} approved team{standings.length === 1 ? "" : "s"} rostered — generate the round-robin when you're ready.</div>
                  </div>
                  <button data-testid="league-generate-schedule" onClick={generate} disabled={busy}
                    className="flex items-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2.5 text-xs hover:opacity-90 disabled:opacity-40">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Generate AI Schedule
                  </button>
                </div>
              ) : (
                <div data-testid="league-draft-schedule">
                  <div className="font-display font-bold uppercase text-sm mb-1">Draft Schedule — {draft.weeks} weeks</div>
                  {draft.insight && <div className="text-xs text-pb-cyan flex items-start gap-1.5 mb-2"><Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />{draft.insight}</div>}
                  <div className="max-h-44 overflow-y-auto text-xs text-zinc-400 space-y-1 mb-3">
                    {draft.chart.map((wk) => (
                      <div key={wk.week}><span className="text-zinc-600 font-mono">Wk {wk.week} · {wk.date}:</span> {wk.matchups.map((m) => `${m.team_a_name} vs ${m.team_b_name}`).join(", ")}</div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="league-publish-schedule" onClick={publish} disabled={busy}
                      className="bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2 text-xs hover:opacity-90 disabled:opacity-40">Publish Season</button>
                    <button data-testid="league-discard-schedule" onClick={discard}
                      className="border border-pb-border text-zinc-400 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:text-white">Discard</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div data-testid="league-standings">
            <div className="flex items-center gap-2 mb-2"><Trophy className="w-4 h-4 text-pb-cyan" /><span className="font-display font-bold uppercase">Standings</span></div>
            {standings.length === 0 ? (
              <div className="text-xs text-zinc-600">No approved teams yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-pb-border/60">
                  <th className="text-left py-2">#</th><th className="text-left py-2">Team</th>
                  <th className="text-center py-2">W</th><th className="text-center py-2">L</th>
                  <th className="text-center py-2">Pts</th><th className="text-center py-2">Bonus</th>
                </tr></thead>
                <tbody>
                  {standings.map((r) => (
                    <tr key={r.team_id} className="border-b border-pb-border/40">
                      <td className="py-2 font-display font-black text-zinc-500">{r.rank}</td>
                      <td className="py-2 font-semibold">{r.name}</td>
                      <td className="py-2 text-center font-display font-black text-pb-cyan">{r.wins}</td>
                      <td className="py-2 text-center font-mono text-zinc-400">{r.losses}</td>
                      <td className="py-2 text-center font-display font-black text-pb-red">{r.team_points}</td>
                      <td className="py-2 text-center font-mono text-amber-400">{r.bonus_points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div data-testid="league-matches">
            <div className="font-display font-bold uppercase mb-2">Match Board</div>
            {matches.length === 0 ? (
              <div className="text-xs text-zinc-600">Schedule not published yet.</div>
            ) : (
              <div className="divide-y divide-pb-border/40">
                {matches.map((m) => <MatchRow key={m.id} m={m} canManage={canManage} onSaved={load} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const PHASES = [["dink_wars_winner", "Dink Wars"], ["transition_wars_winner", "Transition Wars"], ["game_play_winner", "Game Play"]];

function MatchRow({ m, canManage, onSaved }) {
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/bpl/matches/${m.id}/result`, picks);
      toast.success("Result locked in");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unlock = async () => {
    try { await api.post(`/bpl/matches/${m.id}/unlock`); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="py-3" data-testid={`league-match-${m.id}`}>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-xs text-zinc-500 font-mono w-20 shrink-0">{m.date}</span>
        <span className="flex-1 font-semibold">{m.team_a_name} <span className="text-zinc-600">vs</span> {m.team_b_name}</span>
        {m.status === "Locked" && m.scoring ? (
          <>
            {m.scoring.bonus_awarded && <Award className="w-4 h-4 text-amber-400" />}
            <span className="font-display font-bold text-pb-cyan">{m.scoring.match_winner === m.team_a ? m.team_a_name : m.team_b_name} W</span>
            {canManage && <button data-testid={`match-unlock-${m.id}`} onClick={unlock} className="text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white border border-pb-border px-2 py-1">Unlock</button>}
          </>
        ) : (
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-pb-border px-2 py-1">Pending</span>
        )}
      </div>
      {canManage && m.status !== "Locked" && (
        <div className="flex flex-wrap items-end gap-3 mt-2 pl-20">
          {PHASES.map(([k, label]) => (
            <div key={k}>
              <div className="text-[8px] uppercase tracking-widest text-zinc-600 mb-1">{label}</div>
              <div className="flex">
                {["A", "B"].map((s) => (
                  <button key={s} data-testid={`match-${m.id}-${k}-${s}`} onClick={() => setPicks((p) => ({ ...p, [k]: s }))}
                    className={`px-2.5 py-1.5 text-[10px] font-bold uppercase border transition-colors ${picks[k] === s ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                    {s === "A" ? m.team_a_name.slice(0, 10) : m.team_b_name.slice(0, 10)}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button data-testid={`match-save-${m.id}`} onClick={save} disabled={busy || Object.keys(picks).length < 3}
            className="bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2 text-[10px] hover:opacity-90 disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Lock Result"}
          </button>
        </div>
      )}
    </div>
  );
}
